import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type FC,
  type ReactNode,
  type Reducer,
} from 'react'
import debounce from 'lodash/debounce'

import type { CartFragmentFragment } from '__generated__/graphql'
import { type Cart, emptyCart, type CartItem } from 'data/cart'
import { type ProductVariantId, type Product } from 'data/product'
import { trackInternalEvent, trackShopifyEvent, debugLog } from 'lib/analytics'
import { getCookiebotConsent } from 'lib/consent'
import {
  getCart,
  createOrUpdateCart,
  updateCartAttributes,
} from 'shopify/shopifyCart'
import { getGaField } from 'lib/gtag'
import { ProductsContext } from './ProductsProvider'
import { InventoryContext } from './InventoryProvider'
import {
  getCartIdFromStorage,
  clearCartIdFromStorage,
  setCartIdToStorage,
} from 'lib/cartIdStorage'
import { getCampaignParams } from 'lib/campaignParams'
import { buildCartAttribution, sameAttributes } from 'lib/cartAttribution'
import ShopifyCustomerPrivacy from './ShopifyCustomerPrivacy'

// Hard ceiling on how long checkoutCart waits for the best-effort GA4
// attribution work (the gtag read + the Storefront cart-attribute write). If
// it elapses we redirect anyway; the write is sent with keepalive, so the
// browser still finishes it after navigation.
const ATTRIBUTION_BUDGET_MS = 2500

// Persist the GA4 identifiers and the landing's campaign params as cart
// attributes, which Shopify copies onto the order. The server-side webhook
// re-emits the purchase keyed to the original session; the _utm_* keys show the
// campaign on the order even when GA was blocked or consent declined.
const writeCartAttribution = async (
  cart: CartFragmentFragment,
  campaignParams: Record<string, string>
): Promise<void> => {
  const [clientId, sessionId] = await Promise.all([
    getGaField('client_id'),
    getGaField('session_id'),
  ])

  const marketingConsent = getCookiebotConsent('marketing')
  const attributes = buildCartAttribution(cart.attributes, {
    clientId,
    sessionId,
    campaignParams,
    marketingConsent,
  })

  // Still write when only removing keys, e.g. an expired campaign.
  if (sameAttributes(cart.attributes, attributes)) {
    debugLog('cart attributes unchanged; skipping cart write', {
      hasClientId: !!clientId,
    })
    return
  }

  debugLog('writing cart attributes', {
    cartId: cart.id,
    hasClientId: !!clientId,
    hasSessionId: !!sessionId,
    marketingConsent,
    keys: attributes.map((attr) => attr.key),
  })

  await updateCartAttributes(cart.id, attributes)
  debugLog('cart attributes written', { cartId: cart.id })
}

interface StorefrontState {
  cart: Cart
  cartIsOpen: boolean
  checkoutCart: () => Promise<void>
  closeCart: () => void
  openCart: () => void
  remove: (variantId: ProductVariantId) => void
  setQuantity: (variantId: ProductVariantId, quantity: number) => void
  addItem: (variantId: ProductVariantId, quantity: number) => void
}

export const StorefrontContext = createContext<StorefrontState>({
  cart: emptyCart,
  cartIsOpen: false,
  checkoutCart: () => Promise.resolve(),
  closeCart: () => {},
  openCart: () => {},
  remove: () => {},
  addItem: () => {},
  setQuantity: () => {},
})

interface SetQuantityAction {
  type: 'setQuantity'
  variantId: ProductVariantId
  quantity: number
}

interface RemoveAction {
  type: 'remove'
  variantId: ProductVariantId
}

interface ResolveState {
  type: 'resolveState'
  state: Omit<CartState, 'resolved'>
}

interface MergeState {
  type: 'mergeState'
  state: Omit<CartState, 'resolved'>
}

const mapCartFragmentToCartState = (
  cart: CartFragmentFragment
): Omit<CartState, 'resolved'> => ({
  cartId: cart.id,
  items: cart.lines.edges.map<CartStateItem>(({ node }, index) => ({
    id: node.id,
    merchandiseId: node.merchandise.id,
    quantity: node.quantity,
    order: cart.lines.edges.length - index,
  })),
})

type Action = SetQuantityAction | RemoveAction | ResolveState | MergeState

interface CartStateItem {
  id?: string
  merchandiseId: string
  quantity: number
  order: number // kept in descending order
}

interface CartState {
  cartId?: string
  items: CartStateItem[]
  resolved: boolean
}

const descending = (a: CartStateItem, b: CartStateItem) => b.order - a.order

const reducer: Reducer<CartState, Action> = (state, action) => {
  switch (action.type) {
    case 'setQuantity': {
      trackShopifyEvent('updateCartQuantity')

      const existingItemIndex = state.items.findIndex(
        (item) => item.merchandiseId === action.variantId
      )

      let updatedItems = [...state.items]

      if (existingItemIndex !== -1) {
        // Update quantity immutably
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: action.quantity,
        }
      } else {
        // Get the highest order number
        const maxOrder =
          state.items.length > 0
            ? Math.max(...state.items.map((item) => item.order))
            : 0

        // Add new item immutably
        updatedItems = [
          ...updatedItems,
          {
            id: undefined,
            merchandiseId: action.variantId,
            quantity: action.quantity,
            order: maxOrder + 1,
          },
        ]
      }

      return {
        ...state,
        resolved: false,
        items: updatedItems.sort(descending),
      }
    }

    case 'remove': {
      trackShopifyEvent('removeFromCart')

      return {
        ...state,
        resolved: false,
        items: state.items.map((item) => {
          if (item.merchandiseId === action.variantId) {
            item.quantity = 0
          }

          return item
        }),
      }
    }

    case 'resolveState': {
      const newState = { ...action.state, resolved: true }
      return newState
    }

    case 'mergeState': {
      // existing items by merchandiseId for quick lookup
      const existingItems = state.items.reduce<
        Record<string, CartStateItem | undefined>
      >((acc, item) => {
        acc[item.merchandiseId] = item
        return acc
      }, {})

      // if line items are the same length, begin by assuming the state is resolved
      let resolved = state.items.length === action.state.items.length

      // keep existing quantity
      const updatedItems = action.state.items.map((newItem) => {
        const existingItem = existingItems[newItem.merchandiseId]

        // differences mean that the state is not resolved
        if (!existingItem || existingItem.quantity !== newItem.quantity) {
          resolved = false
        }

        if (existingItem) {
          // preserve existing quantity but use the new line item id
          return {
            ...newItem,
            quantity: existingItem.quantity,
            order: existingItem.order,
          }
        }

        return newItem
      })

      return {
        ...action.state,
        items: updatedItems.sort(descending),
        resolved,
      }
    }

    default:
      throw new Error(`Unknown action: ${action}`)
  }
}

const buildCart = (
  products: Product[],
  cartState: CartState,
  variantInventory: Record<string, number>
): Cart => {
  const variants = products.flatMap((p) => p.variants)

  const items = cartState.items.reduce<CartItem[]>(
    (acc, { merchandiseId: variantId, quantity, id }) => {
      const variant = variants.find((v) => v.id === variantId)
      if (variant) {
        acc.push({
          id: id || null,
          title: variant.title,
          quantity,
          price: variant.price,
          variantSlug: variant.slug,
          variantId: variant.id,
        })
      }

      return acc
    },
    []
  )

  const subtotal = items.reduce(
    (acc, i) => acc + Number(i.price) * i.quantity,
    0
  )

  const isValid = items.reduce(
    (acc, i) => acc && variantInventory[i.variantId] >= i.quantity,
    true
  )

  return {
    items,
    subtotalPrice: subtotal.toFixed(2),
    isValid,
  }
}

const resolveCart = async () => {
  const cart = await getCart(getCartIdFromStorage())

  if (!cart) {
    clearCartIdFromStorage()
  }

  return cart
}

const StorefrontProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    items: [],
    cartId: undefined,
    resolved: false,
  })

  // Initialize to false for SSR, then check URL on client mount
  const [cartIsOpen, setCartIsOpen] = useState(false)

  // Check for ?cart=open in URL after hydration
  useEffect(() => {
    if (window.location.search.includes('cart=open')) {
      setCartIsOpen(true)
    }
  }, [])

  const { variantInventory } = useContext(InventoryContext)
  const products = useContext(ProductsContext)

  const setQuantity = (variantId: ProductVariantId, quantity: number) =>
    dispatch({ type: 'setQuantity', variantId, quantity })

  const remove = (variantId: ProductVariantId) => {
    dispatch({ type: 'remove', variantId })
  }

  const addItem = (variantId: ProductVariantId, quantity: number) => {
    trackInternalEvent('addToCart')
    setQuantity(variantId, quantity)
    openCart()
  }

  const closeCart = () => {
    trackInternalEvent('closeCart')
    setCartIsOpen(false)
  }

  const openCart = () => {
    trackInternalEvent('openCart')
    setCartIsOpen(true)
  }

  // load cart
  useEffect(() => {
    let cancelled = false

    resolveCart().then((cart) => {
      if (!cancelled && cart) {
        dispatch({
          type: 'resolveState',
          state: mapCartFragmentToCartState(cart),
        })
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  const updateShopifyCartWithState = useCallback(async (state: CartState) => {
    const cart = await createOrUpdateCart(
      state.cartId,
      state.items.map(({ order: _, ...item }) => item)
    )

    if (cart) {
      setCartIdToStorage(cart.id)

      dispatch({
        type: 'mergeState',
        state: mapCartFragmentToCartState(cart),
      })
    }

    return cart
  }, [])

  const debouncedUpdateShopifyCart = useMemo(
    () => debounce(updateShopifyCartWithState, 500),
    [updateShopifyCartWithState]
  )

  // save state changes to shopify cart
  useEffect(() => {
    if (state.resolved) return

    debouncedUpdateShopifyCart(state)

    return () => debouncedUpdateShopifyCart.cancel()
  }, [state, debouncedUpdateShopifyCart])

  // checkout
  const checkoutCart = async () => {
    trackShopifyEvent('checkout')

    // save possible outstanding changes and get a cart
    const cart = await updateShopifyCartWithState(state)

    if (!cart) return

    const storedParams = getCampaignParams()

    // Best-effort and time-boxed. Never blocks checkout.
    const outcome = await Promise.race([
      writeCartAttribution(cart, storedParams)
        .then(() => 'written' as const)
        .catch((error) => {
          debugLog('attribution write failed', { error: String(error) })
          return 'failed' as const
        }),
      new Promise<'timeout'>((resolve) =>
        setTimeout(() => resolve('timeout'), ATTRIBUTION_BUDGET_MS)
      ),
    ])
    if (outcome === 'timeout') {
      debugLog('attribution budget elapsed; redirecting with write in flight')
    }

    const checkoutUrl = cart.checkoutUrl

    if (checkoutUrl) {
      // utm attribution
      const url = new URL(checkoutUrl)
      for (const [key, value] of Object.entries(storedParams)) {
        url.searchParams.set(key, value)
      }

      window.location.href = url.toString()
    }
  }

  const cart = buildCart(products, state, variantInventory)

  return (
    <StorefrontContext.Provider
      value={{
        cart,
        checkoutCart,
        setQuantity,
        remove,
        cartIsOpen,
        closeCart,
        addItem,
        openCart,
      }}
    >
      {children}
      <ShopifyCustomerPrivacy />
    </StorefrontContext.Provider>
  )
}

export default StorefrontProvider
